/* eslint-disable sonarjs/slow-regex */
/* eslint-disable sonarjs/no-nested-conditional */
/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * mockOverride — DEBUG-ONLY API mock/override hook.
 *
 * Lets a developer route the app's API traffic through the Unity build tool's
 * mock/proxy server (running on their machine) so responses can be overridden
 * with custom HTTP status codes/bodies, while everything else passes through to
 * the real backend.
 *
 * Activation (debug builds only — gated by the __MOCK_ENABLED__ DefinePlugin
 * flag in webpack.config.js so this is fully dead-code-eliminated in release):
 *   - Query param: ?mockHost=<host:port>|<token>   (persisted to localStorage)
 *   - Disable:     ?mockHost=off
 *   - On-device:   the debug overlay's "Mock Host" field (see
 *                  views/common/AppVersionOverlay) — the only route available
 *                  on a TV, where there is no address bar to put a query
 *                  param in. It calls applyMockOverride() / clearMockOverride()
 *                  below, so both entry points write the same storage key and
 *                  nothing downstream has to know which one was used.
 *   - Or set localStorage.OVERRIDE_MOCK_HOST = {"HOST","TOKEN","ENABLE"}
 *
 * The hook rewrites every API base URL to:
 *   http://<host:port>/<token>/p/<base64url(originalBaseUrl)>
 * The generated API client then appends the service path, so the proxy can both
 * match override rules and pass through to the correct upstream per service.
 */

declare const __MOCK_ENABLED__: boolean;

export const MOCK_OVERRIDE_STORAGE_KEY: string = "OVERRIDE_MOCK_HOST";

const STORAGE_KEY = MOCK_OVERRIDE_STORAGE_KEY;

/**
 * Separator between host and token in the single-field form
 * `<host:port>|<token>` — the shape the query param already uses, and the
 * shape the overlay asks for, so a developer can paste the same string into
 * either.
 */
const HOST_TOKEN_SEPARATOR: string = "|";

/**
 * Shape check for `<host:port>|<token>`, used to reject typos before they are
 * persisted. Deliberately loose about the host — it is a LAN address or
 * hostname on someone's desk, not something worth pinning down — and strict
 * only about there being exactly one separator with a non-empty side each.
 */
const MOCK_HOST_PATTERN: RegExp = /^[\w.-]+(?::\d{1,5})?\|[\w-]+$/;

interface MockOverrideConfig {
  HOST: string;
  TOKEN: string;
  ENABLE: boolean;
}

/** Splits `<host:port>|<token>` into its parts, or null when malformed. */
function parseHostToken(value: string): { host: string; token: string } | null {
  const sep: number = value.indexOf(HOST_TOKEN_SEPARATOR);
  if (sep === -1) {
    return null;
  }
  const host: string = value.slice(0, sep).trim();
  const token: string = value.slice(sep + 1).trim();
  if (host === "" || token === "") {
    return null;
  }
  return { host, token };
}

/** True only in debug builds where the DefinePlugin flag is set to true. */
function isMockBuild(): boolean {
  try {
    return typeof __MOCK_ENABLED__ !== "undefined" && __MOCK_ENABLED__ === true;
  } catch {
    return false;
  }
}

function hasBrowserStorage(): boolean {
  return (
    typeof window !== "undefined" && typeof window.localStorage !== "undefined"
  );
}

function readConfig(): MockOverrideConfig | null {
  if (!hasBrowserStorage()) {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<MockOverrideConfig>;
    if (
      parsed.ENABLE === true &&
      typeof parsed.HOST === "string" &&
      typeof parsed.TOKEN === "string" &&
      parsed.HOST !== "" &&
      parsed.TOKEN !== ""
    ) {
      return { HOST: parsed.HOST, TOKEN: parsed.TOKEN, ENABLE: true };
    }
  } catch {
    /* ignore malformed config */
  }
  return null;
}

function writeConfig(config: MockOverrideConfig | null): void {
  if (!hasBrowserStorage()) {
    return;
  }
  try {
    if (config && config.ENABLE && config.HOST !== "" && config.TOKEN !== "") {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
}

/**
 * Parse `?mockHost=` from the current URL and persist it. Accepts
 * `host:port|token` to enable, or `off` to disable.
 */
function applyQueryParam(): void {
  if (typeof window === "undefined" || !window.location) {
    return;
  }
  let value: string | null = null;
  try {
    value = new URLSearchParams(window.location.search).get("mockHost");
  } catch {
    return;
  }
  if (value === null) {
    return;
  }
  if (value === "off" || value === "false") {
    writeConfig(null);
    return;
  }
  const parsed = parseHostToken(value);
  if (parsed !== null) {
    writeConfig({ HOST: parsed.host, TOKEN: parsed.token, ENABLE: true });
  }
}

/** base64url-encode an ASCII string (URLs are ASCII). */
function base64Url(input: string): string {
  const b64: string =
    typeof btoa === "function"
      ? btoa(input)
      : // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (globalThis as any).Buffer
        ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (globalThis as any).Buffer.from(input, "utf8").toString("base64")
        : input;
  return b64.replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/, "");
}

let cachedConfig: MockOverrideConfig | null = null;
let initialised = false;

/**
 * Call once at app bootstrap (debug builds). Reads the query param / storage so
 * later transformBaseUrl() calls are cheap.
 */
export function installMockOverride(): void {
  if (!isMockBuild()) {
    return;
  }
  applyQueryParam();
  cachedConfig = readConfig();
  initialised = true;
  if (cachedConfig) {
    // eslint-disable-next-line no-console
    console.info(
      `[mockOverride] API traffic routed via http://${cachedConfig.HOST}/${cachedConfig.TOKEN}`
    );
  }
}

/**
 * Rewrite an API base URL to go through the mock/proxy server when an override
 * is active. Returns the original URL untouched otherwise.
 */
export function transformBaseUrl(originalBaseUrl: string): string {
  if (!isMockBuild() || !originalBaseUrl) {
    return originalBaseUrl;
  }
  const config = initialised ? cachedConfig : readConfig();
  if (!config) {
    return originalBaseUrl;
  }
  // Don't double-rewrite if it's already pointing at the mock host.
  if (originalBaseUrl.indexOf(`/${config.TOKEN}/p/`) !== -1) {
    return originalBaseUrl;
  }
  return `http://${config.HOST}/${config.TOKEN}/p/${base64Url(originalBaseUrl)}`;
}

/** Whether an override is currently active (for diagnostics/UI). */
export function isMockOverrideActive(): boolean {
  if (!isMockBuild()) {
    return false;
  }
  return (initialised ? cachedConfig : readConfig()) !== null;
}

/* ── On-device entry points (debug overlay) ───────────────────────────── */

/**
 * Whether `value` is a well-formed `<host:port>|<token>` string.
 *
 * Exported so the overlay can reject a typo *before* writing storage and
 * reloading: an unreachable mock host fails every API call, and the only way
 * back is to reopen the overlay, so it is worth catching a mistyped value
 * while the app is still healthy.
 *
 * Built on `parseHostToken()` rather than matching the raw string, so it
 * accepts exactly what `applyMockOverride()` would store — including the
 * whitespace either side of the separator that a paste off a wiki page tends
 * to carry. A validator stricter than the parser would reject values the
 * parser handles perfectly well.
 */
export function isValidMockHostValue(value: string): boolean {
  const parsed = parseHostToken(value.trim());
  if (parsed === null) {
    return false;
  }
  return MOCK_HOST_PATTERN.test(
    parsed.host + HOST_TOKEN_SEPARATOR + parsed.token
  );
}

/**
 * Persists a `<host:port>|<token>` override entered on-device.
 *
 * Returns false — leaving storage untouched — when the build is not a mock
 * build, the value is malformed, or storage is unavailable, so the caller can
 * report the failure instead of reloading into a broken state.
 *
 * Takes effect on the next full document load, exactly like the query-param
 * route: `installMockOverride()` snapshots the config at bootstrap and API
 * clients bake their base URL at construction, so the caller must restart the
 * app (`reloadApplicationAtRoot()`) for this to reach any request.
 */
export function applyMockOverride(value: string): boolean {
  if (!isMockBuild() || !hasBrowserStorage()) {
    return false;
  }
  const parsed = parseHostToken(value.trim());
  if (parsed === null) {
    return false;
  }
  const config: MockOverrideConfig = {
    HOST: parsed.host,
    TOKEN: parsed.token,
    ENABLE: true
  };
  writeConfig(config);
  if (readConfig() === null) {
    return false;
  }
  cachedConfig = config;
  return true;
}

/**
 * Removes the override so the next launch talks to the real backend.
 *
 * Returns true in a release build as well: there is nothing stored to clear,
 * so "no override is in effect" is already satisfied and the caller should not
 * treat that as a failure.
 */
export function clearMockOverride(): boolean {
  if (!isMockBuild()) {
    return true;
  }
  if (!hasBrowserStorage()) {
    return false;
  }
  writeConfig(null);
  cachedConfig = null;
  return readConfig() === null;
}

/**
 * The active override as the single `<host:port>|<token>` string the overlay
 * shows and edits, or empty string when no override is in effect. Reads
 * storage rather than the cache so the overlay reflects what the *next* launch
 * will use.
 */
export function readMockOverrideValue(): string {
  if (!isMockBuild()) {
    return "";
  }
  const config = readConfig();
  if (config === null) {
    return "";
  }
  return config.HOST + HOST_TOKEN_SEPARATOR + config.TOKEN;
}
